import { useState } from 'react';
import type { CSSProperties } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { SvgLogo } from '@actual-app/components/icons/logo';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { css } from '@emotion/css';

import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import type { GaviEstado } from '#components/mobile/travesia/Gavi';
import { SelectorTrato } from '#components/mobile/travesia/SelectorTrato';
import { contextoTrato } from '#components/mobile/travesia/trato';
import type { Trato } from '#components/mobile/travesia/trato';
import { Boton } from '#components/mobile/ui/Boton';
import { color, movimiento } from '#components/mobile/ui/tokens';
import { appName } from '#presupuesto';

/** Pasos de la «Primera travesía» que cuenta la barra (el 0 no se numera). */
export const PASOS_TRAVESIA = 8;

/** Largo máximo del nombre de la persona (como la maqueta). */
export const LARGO_NOMBRE = 30;

/** A partir de aquí Gavi comenta que el nombre es largo. */
const NOMBRE_LARGO = 16;

type Paso0BienvenidaProps = {
  /**
   * Guarda el nombre y la forma de trato (`null` = sin elegir) y abre el
   * Astillero (paso 1).
   */
  onContinuar: (nombre: string, trato: Trato | null) => void;
  /** Sigue sin nombre al Astillero de siempre. */
  onSaltar: () => void;
};

/**
 * Paso 0 de la «Primera travesía»: la bienvenida con la marca y el lema, y
 * Gavi pide el nombre con el que llamar a la persona (pref global
 * `nombrePerfil`, del dispositivo). Con el nombre escrito sale «¿Y cómo te
 * hablo?» (opcional, pref `tratoPerfil`): al elegir, el título y Gavi lo
 * dicen ya con esa forma. Sale en el gestor al tocar «Empezar de cero»
 * cuando aún no hay nombre.
 */
export function Paso0Bienvenida({
  onContinuar,
  onSaltar,
}: Paso0BienvenidaProps) {
  const { t } = useTranslation();
  const [nombre, setNombre] = useState('');
  const [toques, setToques] = useState(0);
  const [trato, setTrato] = useState<Trato | null>(null);
  // Lo último que se tocó fue una forma de trato: Gavi contesta a eso.
  const [reaccionTrato, setReaccionTrato] = useState(false);
  const limpio = nombre.trim();
  const contexto = contextoTrato(trato);

  let mensaje = t(
    "Hi! I'm Gavi, the harbour seagull. I don't steal chips here: I look after euros. What should I call you?",
  );
  let estado: GaviEstado = toques === 0 ? 'vuela' : 'ladea';
  if (toques > 0 && limpio) {
    if (reaccionTrato && trato) {
      mensaje = t(
        'Nice to meet you, {{name}}! We can set sail now. I was born ready.',
        { name: limpio, context: contexto },
      );
      estado = 'aplaude';
    } else {
      mensaje =
        limpio.length > NOMBRE_LARGO
          ? t("Long name. There's room: the logbook is big.")
          : t("{{name}}. It's written down in the logbook.", { name: limpio });
    }
  }

  const continuar = () => {
    if (limpio) {
      onContinuar(limpio, trato);
    }
  };

  return (
    <View data-testid="paso0-bienvenida" style={pantalla}>
      <BarraTravesia paso={0} onSaltar={onSaltar} />
      <View
        style={{
          gap: 14,
          padding: '24px 16px calc(24px + env(safe-area-inset-bottom))',
          maxWidth: 520,
          width: '100%',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        <View
          style={{
            alignItems: 'center',
            gap: 6,
            margin: '4px 0 6px',
            textAlign: 'center',
          }}
        >
          <SvgLogo width={64} height={64} aria-hidden />
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 800,
              lineHeight: 1.25,
              color: color.fg,
            }}
          >
            {t('Welcome to {{appName}}', { appName, context: contexto })}
          </h1>
          <Text style={{ fontSize: 14, color: color.fg2 }}>
            <Trans>Budget your course</Trans>
          </Text>
        </View>

        <Bocadillo
          texto={mensaje}
          estado={estado}
          clave={toques}
          duracion={reaccionTrato && trato ? 1200 : limpio ? 900 : 600}
          data-testid="paso0-gavi"
        />

        <View>
          <label htmlFor="paso0-nombre" style={etiqueta}>
            <Trans>Name</Trans>
          </label>
          <input
            id="paso0-nombre"
            data-testid="paso0-nombre"
            autoComplete="given-name"
            maxLength={LARGO_NOMBRE}
            placeholder={t('e.g. Alex')}
            value={nombre}
            onChange={e => {
              setNombre(e.currentTarget.value);
              setToques(n => n + 1);
              setReaccionTrato(false);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                continuar();
              }
            }}
            className={campo}
          />
        </View>

        {limpio && (
          <View data-testid="paso0-trato">
            <Text style={pregunta}>
              <Trans>How should I talk to you?</Trans>
            </Text>
            <SelectorTrato
              etiqueta={t('How should I talk to you?')}
              testId="paso0-trato"
              valor={trato}
              onChange={nuevo => {
                setTrato(nuevo);
                setToques(n => n + 1);
                setReaccionTrato(true);
              }}
            />
            <Text style={notaTrato}>
              <Trans>
                Just to get the words right. You can change it in Settings.
              </Trans>
            </Text>
          </View>
        )}

        <Boton
          bloque
          data-testid="paso0-continuar"
          isDisabled={!limpio}
          onPress={continuar}
          // Como la maqueta: el verde de siempre, apagado, mientras falta el
          // nombre (el texto sigue diciendo qué hace).
          style={
            limpio
              ? undefined
              : {
                  backgroundColor: color.accent,
                  color: color.accentInk,
                  opacity: 0.4,
                }
          }
        >
          <Trans>Continue</Trans>
        </Boton>
      </View>
    </View>
  );
}

type BarraTravesiaProps = {
  /** 0 = bienvenida (sin «Paso N de 8»). */
  paso: number;
  onSaltar: () => void;
  style?: CSSProperties;
};

/**
 * Barra de arriba de la travesía: progreso, «Paso N de 8» y «Saltar». La
 * comparten el paso 0 y el Astillero en modo travesía.
 */
export function BarraTravesia({ paso, onSaltar, style }: BarraTravesiaProps) {
  const { t } = useTranslation();
  const fraccion = Math.max(0, Math.min(1, paso / PASOS_TRAVESIA));
  return (
    <View
      data-testid="barra-travesia"
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0,
        padding: 'env(safe-area-inset-top) 16px 0',
        minHeight: 'calc(44px + env(safe-area-inset-top))',
        boxSizing: 'border-box',
        backgroundColor: color.bg,
        ...style,
      }}
    >
      <View
        role="progressbar"
        aria-label={t('Tour progress')}
        aria-valuemin={0}
        aria-valuemax={PASOS_TRAVESIA}
        aria-valuenow={paso}
        style={{
          flex: 1,
          height: 4,
          borderRadius: 99,
          overflow: 'hidden',
          backgroundColor: color.surface3,
        }}
      >
        <View
          style={{
            width: `${fraccion * 100}%`,
            height: '100%',
            borderRadius: 99,
            backgroundColor: color.accent,
            transition: `width 400ms ${movimiento.muelle}`,
          }}
        />
      </View>
      {paso > 0 && (
        <Text
          data-testid="barra-travesia-paso"
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: color.fg3,
            whiteSpace: 'nowrap',
          }}
        >
          {t('Step {{step}} of {{total}}', {
            step: paso,
            total: PASOS_TRAVESIA,
          })}
        </Text>
      )}
      <Button
        variant="bare"
        data-testid="travesia-saltar"
        onPress={onSaltar}
        style={{
          minHeight: 44,
          padding: '0 2px',
          fontSize: 13,
          fontWeight: 700,
          color: color.accent,
          whiteSpace: 'nowrap',
          backgroundColor: 'transparent',
        }}
      >
        <Trans>Skip</Trans>
      </Button>
    </View>
  );
}

const pantalla = {
  position: 'fixed',
  inset: 0,
  // Por encima del aviso de sesión de la gestión (zIndex 4000), que tapaba
  // «Saltar» con servidor.
  zIndex: 4001,
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor: color.bg,
  color: color.fg,
} as const;

const etiqueta = {
  display: 'block',
  margin: '0 2px 6px',
  fontSize: 13,
  fontWeight: 700,
  color: color.fg2,
} as const;

const pregunta = {
  display: 'block',
  margin: '0 2px 8px',
  fontSize: 14,
  fontWeight: 800,
  color: color.fg,
} as const;

const notaTrato = {
  display: 'block',
  margin: '6px 2px 0',
  fontSize: 12.5,
  lineHeight: 1.35,
  fontWeight: 600,
  color: color.fg3,
} as const;

const campo = css({
  width: '100%',
  minHeight: 50,
  boxSizing: 'border-box',
  borderRadius: 14,
  backgroundColor: color.surface2,
  border: `1px solid ${color.line}`,
  padding: '0 14px',
  fontSize: 16,
  fontFamily: 'inherit',
  color: color.fg,
  '::placeholder': { color: color.fg3 },
  ':focus': {
    outline: 'none',
    borderColor: color.accent,
    boxShadow: `0 0 0 2px ${color.accent}`,
  },
});
